import React, { useState } from 'react';
import { Typography, Paper, Link, Stack, Box } from '@mui/material';
import { DebouncedTextField } from '@/components/common/DebouncedTextField';
import { DataPackageDto, PrivilegeEnum } from '@/api/ApiClient';
import DetailField from '@/components/common/DetailField';
import { Footer } from '@/components/details/Footer';
import { useBackendStore } from '@/stores/backendStore';
import { useTree } from '@/hooks/useTree';
import { useTreeSelection } from '@/hooks/useNodeState';
import { useDataPackageData, useIsDataModified } from '@/hooks/useTreeDetailSubscriptions';
import { useSnackbar } from '@/hooks/useSnackbar';
import { log } from '@/lib/utils/logger';
import { useUserResolution } from '@/lib/options/useUserResolution';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import InlineImages from '@/components/common/InlineImages';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { validateRequiredToSave } from '@/lib/utils/saveValidation';
import { createSimulationDatasetSaveValidation } from '@/lib/validation/saveValidationBuilders';
import { toUpdateSimulationDatasetRequest } from '@/lib/entity/entitySaveUtils';
import { stripDoiPrefix, toDoiHref } from '@/lib/utils/doiLinks';

export const SimulationDatasetDetails: React.FC = () => {
  const backend = useBackendStore.getState().getBackend();
  const { resolveUser } = useUserResolution();
  const { setDataModified, updateData } = useTree();
  const { selectedNode } = useTreeSelection();
  const { showSnackbar } = useSnackbar();

  const currentData = useDataPackageData(selectedNode);

  // Compute isReadOnly during render - derived from modification state and manual edit mode
  const dataId = selectedNode?.dataId;
  const isModified = useIsDataModified(dataId);
  const [isManuallyEditing, setIsManuallyEditing] = useState(false);
  const isReadOnly = !isModified && !isManuallyEditing;

  // Validation state
  const [titleError, setTitleError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);

  if (!currentData || !selectedNode) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Select a node to view details
        </Typography>
      </Box>
    );
  }

  const handleModification = (modified: Partial<DataPackageDto>) => {
    const updated = { ...currentData, ...modified };
    updateData(selectedNode.dataId, updated);
    setDataModified(selectedNode.dataId, true);

    // Clear validation errors when user starts typing
    if (modified.label !== undefined) {
      setTitleError(false);
    }
    if (modified.description !== undefined) {
      setDescriptionError(false);
    }
  };

  // Validation function
  const isValid = () => {
    if (!currentData) return false;
    return validateRequiredToSave(createSimulationDatasetSaveValidation(currentData)).isValid;
  };

  const handleSave = async () => {
    if (!currentData?.id) return;

    const saveValidation = validateRequiredToSave(createSimulationDatasetSaveValidation(currentData));
    setTitleError(!!saveValidation.fieldErrors.label);
    setDescriptionError(!!saveValidation.fieldErrors.description);

    if (!saveValidation.isValid) {
      showSnackbar('Please fill out all required fields', 'error');
      return;
    }

    try {
      const updateRequest = toUpdateSimulationDatasetRequest(currentData);
      const updated = await backend.data.update(currentData.id.toString(), updateRequest);
      updateData(selectedNode.dataId, updated);
      setDataModified(selectedNode.dataId, false);
      setIsManuallyEditing(false);
      showSnackbar('Simulation dataset saved successfully', 'success');
    } catch (error) {
      log.error('[SimulationDatasetDetails] Failed to save dataset:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to save simulation dataset.', 'error');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="h6" gutterBottom>
            Simulation Dataset Details
          </Typography>

          {/* Image Section */}
          <InlineImages title="Dataset Images" />

          <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              {/* Title - Full Width */}
              <DetailField
                label="Title"
                value={currentData.label || 'No title provided'}
                isReadOnly={isReadOnly}
                width={{ xs: '100%' }}
              >
                <DebouncedTextField
                  value={currentData.label || ''}
                  onUpdate={(value) => handleModification({ label: value })}
                  fullWidth
                  size="small"
                  error={titleError}
                  helperText={titleError ? 'Title is required' : ''}
                />
              </DetailField>

              {/* Description - Full Width */}
              <DetailField
                label="Description"
                value={
                  currentData.description ? (
                    <span dangerouslySetInnerHTML={{ __html: currentData.description }} />
                  ) : (
                    'No description provided'
                  )
                }
                isReadOnly={isReadOnly}
                width={{ xs: '100%' }}
              >
                <Box>
                  <RichTextEditor
                    value={currentData.description ?? ''}
                    onChange={(content) => handleModification({ description: content })}
                  />
                  {descriptionError ? (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      Description is required
                    </Typography>
                  ) : null}
                </Box>
              </DetailField>

              {/* DOI and Publication - Side by Side */}
              {currentData.doi || currentData.publicationDoi || !isReadOnly ? (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {/* DOI - only show if present or in edit mode */}
                  {currentData.doi || !isReadOnly ? (
                    <DetailField
                      label="DOI"
                      value={
                        currentData.doi ? (
                          <Link href={toDoiHref(currentData.doi)} target="_blank" rel="noopener">
                            {stripDoiPrefix(currentData.doi)}
                          </Link>
                        ) : (
                          'No DOI provided'
                        )
                      }
                      isReadOnly={isReadOnly}
                      width={{ xs: '100%', sm: '48%' }}
                    >
                      <DebouncedTextField
                        value={currentData.doi || ''}
                        onUpdate={(value) => handleModification({ doi: value })}
                        fullWidth
                        size="small"
                        placeholder="Enter DOI"
                      />
                    </DetailField>
                  ) : null}

                  {/* Publication - only show if present or in edit mode */}
                  {currentData.publicationDoi || !isReadOnly ? (
                    <DetailField
                      label="Publication"
                      value={
                        currentData.publicationDoi ? (
                          <Link href={toDoiHref(currentData.publicationDoi)} target="_blank" rel="noopener">
                            {stripDoiPrefix(currentData.publicationDoi)}
                          </Link>
                        ) : (
                          'No publication DOI provided'
                        )
                      }
                      isReadOnly={isReadOnly}
                      width={{ xs: '100%', sm: '48%' }}
                    >
                      <DebouncedTextField
                        value={currentData.publicationDoi || ''}
                        onUpdate={(value) => handleModification({ publicationDoi: value })}
                        fullWidth
                        size="small"
                        placeholder="Enter publication DOI"
                      />
                    </DetailField>
                  ) : null}
                </Box>
              ) : null}
            </Stack>
          </Paper>
        </Stack>
      </Box>

      {/* Footer - Fixed at bottom */}
      <Box sx={{ flexShrink: 0 }}>
        <Footer
          lastChangedDate={currentData.lastChangedDate}
          lastChangedBy={resolveUser(currentData.lastChangedByUser || '')}
          status={currentData.status}
          isReadOnly={isReadOnly}
          setIsReadOnly={(val) => setIsManuallyEditing(!val)}
          onSave={handleSave}
          privilege={PrivilegeEnum.DataPackage}
          isFormValid={isValid()}
        />
      </Box>
    </Box>
  );
};
